import { SejourSummary } from '@scripts/gform-sejour/sejour-summary.js';
import { QuantityControls } from '@scripts/gform-sejour/quantity-controls.js';
import { ParticipantsManager } from '@scripts/gform-sejour/participants-manager.js';
import { OptionsValidator } from '@scripts/gform-sejour/options-validator.js';
import { EventDispatcher, EventDispatcherEvent } from '@scripts/gform-sejour/event-dispatcher.js';
import { TrackingManager } from '@scripts/gform-sejour/tracking-manager.js';

/**
 * For type safety in event handled
 */
export class GFormSejourEvents extends EventDispatcherEvent {
    static PAGE_CHANGE = 'page_change';
}
export class GFormSejour extends EventDispatcher {
    /**
     * For better reusability, use a parent class that handle the event system
     */
    static EventPrefix = 'g_form_sejour';

    /**
     * For security, check that the event exists and is dispatchable
     */
    static EventTypes = GFormSejourEvents.values();
    constructor() {
        super();
        this.formWrapper = document.querySelector('.gform_wrapper');
        if (!this.formWrapper) return;

        // Éviter les initialisations multiples
        if (this.formWrapper.dataset.gformSejourInit) return;
        this.formWrapper.dataset.gformSejourInit = 'true';

        this.formId = this.formWrapper.id?.match(/gform_wrapper_(\d+)/)?.[1];
        if (!this.formId) return;

        this.isSejourSpecific = this.formWrapper.classList.contains('gf-sejour-specific_wrapper');

        this.config = {
            depositPercentage: 0.3
        };

        this.prices = {
            adult: 0,
            child: 0,
            adultPension: 0,
            childPension: 0,
            transfer: 0
        };

        this.state = {
            adults: 1,
            children: 0,
            accommodation: null,
            accommodationPrice: 0,
            accommodationPricePerPerson: false,
            adultPensions: 0,
            childPensions: 0,
            transferCount: 0,
            isFullPayment: false,
            discountAmount: 0,
            discountName: ''
        };

        // Récupérer la page actuelle depuis le DOM
        this.currentPage = this.getInitialPageNumber();
        this.previousPage = parseInt(sessionStorage.getItem('gform_previous_page') || '1');

        // Vérifier si on revient en arrière au chargement
        const storedPage = parseInt(sessionStorage.getItem('gform_current_page') || '1');
        if (this.currentPage < storedPage) {
            this.resetStepsAfterOnLoad(this.currentPage);
        }
        sessionStorage.setItem('gform_previous_page', storedPage.toString());
        sessionStorage.setItem('gform_current_page', this.currentPage.toString());
        this.init();
    }

    getInitialPageNumber() {
        const pages = this.formWrapper.querySelectorAll('.gform_page');
        for (const page of pages) {
            if (!page.style.display || page.style.display !== 'none') {
                const pageId = page.id;
                const match = pageId.match(/gform_page_\d+_(\d+)/);
                if (match) {
                    return parseInt(match[1]);
                }
            }
        }
        return 1;
    }

    resetStepsAfterOnLoad(pageNumber) {
        // Reset étape 3 (pensions et transfert) si on revient à l'étape 1 ou 2
        if (pageNumber <= 2) {
            // Reset pensions via classes CSS custom
            const adultField = this.formWrapper.querySelector('.gf-sejour-pension-adult');
            const childField = this.formWrapper.querySelector('.gf-sejour-pension-child');
            const transferField = this.formWrapper.querySelector('.gf-sejour-transfer');

            if (adultField) {
                const input = adultField.querySelector('.ginput_quantity');
                if (input) input.value = 0;
            }
            if (childField) {
                const input = childField.querySelector('.ginput_quantity');
                if (input) input.value = 0;
            }
            if (transferField) {
                const input = transferField.querySelector('.ginput_quantity');
                if (input) input.value = 0;
            }
        }
    }

    init() {
        this.cleanFormTitle();
        this.extractPricesFromForm();
        this.injectReserveBanner();
        this.injectSejourSummary();
        this.injectPaymentInfo();
        this.injectCustomSpinner();
        this.enhanceAccommodationCards();

        if (this.isSejourSpecific) {
            this.enhancePensionSection();
            this.enhancePensionDultSection();
            this.enhanceTransferSection();
            setTimeout(() => {
                this.injectSummaryAroundCoupon();
                this.updateState();
            }, 500);
        }

        this.trackingManager = new TrackingManager();
        this.summary = new SejourSummary(this);
        this.quantityControls = new QuantityControls(this);
        this.participantsManager = new ParticipantsManager(this);
        this.optionsValidator = new OptionsValidator(this);

        this.bindEvents();
        this.updateState();
    }

    cleanFormTitle() {
        const titleEl = this.formWrapper.querySelector('.gform_title');
        if (!titleEl) return;

        const newTitleField = this.formWrapper.querySelector('.gform-new-title input');
        if (newTitleField && newTitleField.value) {
            titleEl.textContent = newTitleField.value;
        } else if (titleEl.textContent.includes('/')) {
            titleEl.textContent = titleEl.textContent.split('/')[0].trim();
        }
    }

    extractPricesFromForm() {
        // Utiliser les classes CSS custom pour trouver les champs
        const adultsField = this.formWrapper.querySelector('.gf-sejour-adults');
        if (adultsField) {
            const priceInput = adultsField.querySelector('input[id^="ginput_base_price"]');
            if (priceInput) {
                this.prices.adult = this.parsePriceString(priceInput.value);
            }
        }

        const childrenField = this.formWrapper.querySelector('.gf-sejour-children');
        if (childrenField) {
            const priceInput = childrenField.querySelector('input[id^="ginput_base_price"]');
            if (priceInput) {
                this.prices.child = this.parsePriceString(priceInput.value);
            }
        }

        const adultPensionField = this.formWrapper.querySelector('.gf-sejour-pension-adult');
        if (adultPensionField) {
            const priceInput = adultPensionField.querySelector('input[id^="ginput_base_price"]');
            if (priceInput) {
                this.prices.adultPension = this.parsePriceString(priceInput.value);
            }
        }

        const childPensionField = this.formWrapper.querySelector('.gf-sejour-pension-child');
        if (childPensionField) {
            const priceInput = childPensionField.querySelector('input[id^="ginput_base_price"]');
            if (priceInput) {
                this.prices.childPension = this.parsePriceString(priceInput.value);
            }
        }

        const transferField = this.formWrapper.querySelector('.gf-sejour-transfer');
        if (transferField) {
            const priceInput = transferField.querySelector('input[id^="ginput_base_price"]');
            if (priceInput) {
                this.prices.transfer = this.parsePriceString(priceInput.value);
            }
        }
    }

    parsePriceString(priceStr) {
        if (!priceStr) return 0;
        // Format européen : 1.050,00 € -> enlever les points (séparateur milliers), remplacer virgule par point
        const cleaned = priceStr
            .replace(/[^\d,.-]/g, '')  // Garder uniquement chiffres, virgule, point, tiret
            .replace(/\./g, '')         // Enlever les points (séparateur milliers)
            .replace(',', '.');          // Remplacer virgule par point (décimales)
        return parseFloat(cleaned) || 0;
    }

    injectReserveBanner() {
        const existingBanner = document.querySelector('.gf-reserve-banner');
        if (existingBanner) return;

        const bannerHTML = `<div class="gf-reserve-banner">Réserver</div>`;
        this.formWrapper.insertAdjacentHTML('beforebegin', bannerHTML);
    }

    injectSejourSummary() {
        const existingSummary = this.formWrapper.querySelector('.gf-sejour-summary');
        if (existingSummary) return;

        // Récupérer les infos du séjour depuis le champ caché
        const infoSejourField = this.formWrapper.querySelector('.gf-infos-sejour p');
        const infoSejourText = infoSejourField ? infoSejourField.innerHTML : '';

        // Pour le formulaire spécifique : créer 2 blocs pour laisser le coupon au milieu
        if (this.isSejourSpecific) {
        } else {
            // Pour les autres formulaires : un seul bloc comme avant
            const summaryHTML = `
                <div class="gf-sejour-summary">
                    <h3 class="gf-sejour-summary__title">Votre séjour</h3>
                    <div class="gf-sejour-summary__subtitle">${infoSejourText}</div>
                    <div class="gf-sejour-summary__items">
                        <div class="gf-sejour-summary__item" data-summary="adults">
                            <div class="gf-sejour-summary__label">
                                Adultes
                                <span class="gf-sejour-summary__detail" data-detail></span>
                            </div>
                            <div class="gf-sejour-summary__value" data-value>0€</div>
                        </div>
                        <div class="gf-sejour-summary__item gf-sejour-summary__item--hidden" data-summary="children">
                            <div class="gf-sejour-summary__label">
                                Enfants
                                <span class="gf-sejour-summary__detail" data-detail></span>
                            </div>
                            <div class="gf-sejour-summary__value" data-value>0€</div>
                        </div>
                        <div class="gf-sejour-summary__item gf-sejour-summary__item--hidden" data-summary="accommodation">
                            <div class="gf-sejour-summary__label">
                                Hébergement : <span data-name></span>
                                <span class="gf-sejour-summary__detail" data-detail></span>
                            </div>
                            <div class="gf-sejour-summary__value" data-value>0€</div>
                        </div>
                        <div class="gf-sejour-summary__item gf-sejour-summary__item--hidden" data-summary="pension-adult">
                            <div class="gf-sejour-summary__label">
                                Pension complète adulte
                                <span class="gf-sejour-summary__detail" data-detail></span>
                            </div>
                            <div class="gf-sejour-summary__value" data-value>0€</div>
                        </div>
                        <div class="gf-sejour-summary__item gf-sejour-summary__item--hidden" data-summary="pension-child">
                            <div class="gf-sejour-summary__label">
                                Pension complète enfant
                                <span class="gf-sejour-summary__detail" data-detail></span>
                            </div>
                            <div class="gf-sejour-summary__value" data-value>0€</div>
                        </div>
                        <div class="gf-sejour-summary__item gf-sejour-summary__item--hidden" data-summary="transfer">
                            <div class="gf-sejour-summary__label">
                                Transfert depuis la gare
                                <span class="gf-sejour-summary__detail" data-detail></span>
                            </div>
                            <div class="gf-sejour-summary__value" data-value>0€</div>
                        </div>
                    </div>
                    <div class="gf-sejour-summary__divider"></div>
                    <div class="gf-sejour-summary__subtotal">
                        <div class="gf-sejour-summary__subtotal-label">Sous-total</div>
                        <div class="gf-sejour-summary__subtotal-value" data-subtotal>750€</div>
                    </div>
                    <div class="gf-sejour-summary__discount gf-sejour-summary__discount--hidden" data-summary="discount">
                        <div class="gf-sejour-summary__discount-label">
                            <span class="gf-sejour-summary__discount-prefix">Code promo :</span>
                            <span class="gf-sejour-summary__discount-name" data-discount-name>-50%</span>
                        </div>
                        <div class="gf-sejour-summary__discount-value" data-discount-value>-0€</div>
                    </div>
                    <div class="gf-sejour-summary__divider"></div>
                    <div class="gf-sejour-summary__total">
                        <div class="gf-sejour-summary__total-label">Total</div>
                        <div class="gf-sejour-summary__total-value" data-total>750€</div>
                    </div>
                    <div class="gf-sejour-summary__divider"></div>
                    <div class="gf-sejour-summary__deposit">
                        <div class="gf-sejour-summary__deposit-label">Dû à la réservation</div>
                        <div class="gf-sejour-summary__deposit-value" data-deposit>375€</div>
                    </div>
                </div>
            `;

            const pages = this.formWrapper.querySelectorAll('.gform_page');
            if (pages.length > 0) {
                pages.forEach(page => {
                    const footer = page.querySelector('.gform_page_footer');
                    if (footer) {
                        footer.insertAdjacentHTML('beforebegin', summaryHTML);
                    }
                });
            } else {
                const footer = this.formWrapper.querySelector('.gform_footer');
                if (footer) {
                    footer.insertAdjacentHTML('beforebegin', summaryHTML);
                }
            }
        }
    }

    injectSummaryAroundCoupon() {

        // Récupérer les infos du séjour depuis le champ caché
        const infoSejourField = this.formWrapper.querySelector('.gf-infos-sejour p');
        const infoSejourText = infoSejourField ? infoSejourField.innerHTML : '';

        const couponField = this.formWrapper.querySelector('[id^="field_"][class*="coupon"]');
        console.log('[GFormSejour] couponField:', couponField);

        if (!couponField) {
            console.log('[GFormSejour] Impossible de repositionner - coupon manquant');
            return;
        }

        // Créer le bloc 1 du résumé
        const summaryTopHTML = `
            <div class="gf-sejour-summary gf-sejour-summary-top">
                <h3 class="gf-sejour-summary__title">Votre séjour</h3>
                <div class="gf-sejour-summary__subtitle">${infoSejourText}</div>
                <div class="gf-sejour-summary__items">
                    <div class="gf-sejour-summary__item" data-summary="adults">
                        <div class="gf-sejour-summary__label">
                            Adultes
                            <span class="gf-sejour-summary__detail" data-detail></span>
                        </div>
                        <div class="gf-sejour-summary__value" data-value>0€</div>
                    </div>
                    <div class="gf-sejour-summary__item gf-sejour-summary__item--hidden" data-summary="children">
                        <div class="gf-sejour-summary__label">
                            Enfants
                            <span class="gf-sejour-summary__detail" data-detail></span>
                        </div>
                        <div class="gf-sejour-summary__value" data-value>0€</div>
                    </div>
                    <div class="gf-sejour-summary__item gf-sejour-summary__item--hidden" data-summary="accommodation">
                        <div class="gf-sejour-summary__label">
                            Hébergement : <span data-name></span>
                            <span class="gf-sejour-summary__detail" data-detail></span>
                        </div>
                        <div class="gf-sejour-summary__value" data-value>0€</div>
                    </div>
                    <div class="gf-sejour-summary__item gf-sejour-summary__item--hidden" data-summary="pension-adult">
                        <div class="gf-sejour-summary__label">
                            Pension complète adulte
                            <span class="gf-sejour-summary__detail" data-detail></span>
                        </div>
                        <div class="gf-sejour-summary__value" data-value>0€</div>
                    </div>
                    <div class="gf-sejour-summary__item gf-sejour-summary__item--hidden" data-summary="pension-child">
                        <div class="gf-sejour-summary__label">
                            Pension complète enfant
                            <span class="gf-sejour-summary__detail" data-detail></span>
                        </div>
                        <div class="gf-sejour-summary__value" data-value>0€</div>
                    </div>
                    <div class="gf-sejour-summary__item gf-sejour-summary__item--hidden" data-summary="transfer">
                        <div class="gf-sejour-summary__label">
                            Transfert depuis la gare
                            <span class="gf-sejour-summary__detail" data-detail></span>
                        </div>
                        <div class="gf-sejour-summary__value" data-value>0€</div>
                    </div>
                </div>
                <div class="gf-sejour-summary__divider"></div>
                <div class="gf-sejour-summary__subtotal">
                    <div class="gf-sejour-summary__subtotal-label">Sous-total</div>
                    <div class="gf-sejour-summary__subtotal-value" data-subtotal>750€</div>
                </div>
            </div>
        `;

        // Injecter le bloc 1 AVANT le coupon
        couponField.parentNode.insertBefore(document.createElement('div'), couponField).outerHTML = summaryTopHTML;

        // Créer et injecter le bloc 2 APRÈS le coupon
        const existingBottom = this.formWrapper.querySelector('.gf-sejour-summary-bottom');
        if (!existingBottom) {
            const summaryBottomHTML = `
                <div class="gf-sejour-summary gf-sejour-summary-bottom">
                    <div class="gf-sejour-summary__discount gf-sejour-summary__discount--hidden" data-summary="discount">
                        <div class="gf-sejour-summary__discount-label">
                            <span class="gf-sejour-summary__discount-prefix">Code promo :</span>
                            <span class="gf-sejour-summary__discount-name" data-discount-name>-50%</span>
                        </div>
                        <div class="gf-sejour-summary__discount-value" data-discount-value>-0€</div>
                    </div>
                    <div class="gf-sejour-summary__divider"></div>
                    <div class="gf-sejour-summary__total">
                        <div class="gf-sejour-summary__total-label">Total</div>
                        <div class="gf-sejour-summary__total-value" data-total>750€</div>
                    </div>
                    <div class="gf-sejour-summary__divider"></div>
                    <div class="gf-sejour-summary__deposit">
                        <div class="gf-sejour-summary__deposit-label">Dû à la réservation</div>
                        <div class="gf-sejour-summary__deposit-value" data-deposit>375€</div>
                    </div>
                    <div class="gf-sejour-summary__payment-notice">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="10" cy="10" r="10" fill="currentColor"/>
                            <path d="M10 14V9M10 6.5V6" stroke="#FAE8D5" stroke-width="1.5" stroke-linecap="round"/>
                        </svg>
                        <p>Vous réglez seulement 30% aujourd'hui, puis le solde 30 jours avant le départ.</p>
                    </div>
                </div>
            `;
            couponField.parentNode.insertAdjacentHTML('afterend', summaryBottomHTML);
            console.log('[GFormSejour] Résumé injecté autour du coupon');
        }
    }

    injectPaymentInfo() {
        const footers = this.formWrapper.querySelectorAll('.gform_page_footer, .gform_footer');
        footers.forEach(footer => {
            if (footer.querySelector('.gf-payment-info')) return;

            const paymentInfoHTML = `
                <div class="gf-payment-info">
                    <img src="https://makefire.fr/wp-content/uploads/2025/10/Groupe-3031.png" alt="Paiement sécurisé" class="gf-payment-info__image" />
                    <p class="gf-payment-info__contact">Paiement en <strong>2 fois sans frais</strong><br><strong>Annulation gratuite</strong> 30 jours avant le départ</p>
                    <p><strong>Besoin d'un renseignement ?</strong></p>
                    <p>Contactez-nous : +33 7 66 42 57 85</p>
                </div>
            `;
            footer.insertAdjacentHTML('beforeend', paymentInfoHTML);
        });

        if (this.isSejourSpecific) {
            setTimeout(() => this.moveInfosAfterSummaryBottom(), 600);
        }
    }

    moveInfosAfterSummaryBottom() {
        const summaryBottom = this.formWrapper.querySelector('.gf-sejour-summary-bottom');
        const infosStripe = this.formWrapper.querySelector('.gf-infos-stripe');
        const infosPaiement = this.formWrapper.querySelector('.gf-infos-paiement');

        if (summaryBottom && infosStripe) {
            summaryBottom.insertAdjacentElement('afterend', infosStripe);
        }
        if (summaryBottom && infosPaiement) {
            // Insérer après infosStripe s'il existe, sinon après summaryBottom
            const insertAfter = infosStripe || summaryBottom;
            insertAfter.insertAdjacentElement('afterend', infosPaiement);
        }

        this.wrapImageAndFirstBullets();
    }

    wrapImageAndFirstBullets() {
        const image = this.formWrapper.querySelector('.gf-makefire-img');
        const field127 = this.formWrapper.querySelector('#field_14_127');

        if (!image || !field127) return;

        const wrapper = document.createElement('div');
        wrapper.className = 'gf-image-bullets-wrapper';

        image.parentNode.insertBefore(wrapper, image);

        wrapper.appendChild(image);
        wrapper.appendChild(field127);
    }

    injectCustomSpinner() {
        if (this.formWrapper.querySelector('.gf-custom-spinner')) return;

        const spinnerHTML = `
            <div class="gf-custom-spinner">
                <div class="gf-custom-spinner__circle"></div>
            </div>
        `;
        this.formWrapper.insertAdjacentHTML('afterbegin', spinnerHTML);
    }

    showSpinner() {
        this.formWrapper.classList.add('gf-loading');
    }

    hideSpinner() {
        this.formWrapper.classList.remove('gf-loading');
    }

    enhanceAccommodationCards() {
        const accommodationField = this.formWrapper.querySelector('.gf-sejour-accommodation');
        if (!accommodationField) return;

        // Ajouter l'étoile required sur le titre de section hébergement
        // Trouver la section qui précède le champ hébergement
        const prevSibling = accommodationField.previousElementSibling;
        if (prevSibling && prevSibling.classList.contains('gsection')) {
            const sectionTitle = prevSibling.querySelector('.gsection_title');
            if (sectionTitle && !sectionTitle.querySelector('.gfield_required')) {
                const requiredSpan = document.createElement('span');
                requiredSpan.className = 'gfield_required';
                requiredSpan.innerHTML = '<span class="gfield_required_asterisk">*</span>';
                sectionTitle.appendChild(requiredSpan);
            }
        }

        const labels = accommodationField.querySelectorAll('.gchoice label');
        labels.forEach(label => {
            // Vérifier si déjà transformé
            if (label.querySelector('.acc-radio-circle')) return;

            const accTitle = label.querySelector('.acc-title');
            if (accTitle) {
                // Créer le cercle radio
                const radioCircle = document.createElement('span');
                radioCircle.className = 'acc-radio-circle';

                // Insérer le cercle avant le titre
                accTitle.parentNode.insertBefore(radioCircle, accTitle);
            }

        });
    }

    enhancePensionSection() {
        const image = this.formWrapper.querySelector('.gf-sejour-pension-image');
        const adult = this.formWrapper.querySelector('.gf-sejour-pension-adult');
        const child = this.formWrapper.querySelector('.gf-sejour-pension-child');

        if (image && adult && child) {
            const wrapper = document.createElement('div');
            wrapper.className = 'pension-grid-wrapper';

            image.parentNode.insertBefore(wrapper, image);
            wrapper.appendChild(image);
            wrapper.appendChild(adult);
            wrapper.appendChild(child);
        }
    }


    enhancePensionDultSection() {
        const image = this.formWrapper.querySelector('.gf-sejour-pension-img');
        const field = this.formWrapper.querySelector('.gf-sejour-pen-dult');

        if (image && field) {
            const wrapper = document.createElement('div');
            wrapper.className = 'pension-dult-wrapper';

            image.parentNode.insertBefore(wrapper, image);
            wrapper.appendChild(image);
            wrapper.appendChild(field);
        }
    }

    enhanceTransferSection() {
        const icon = this.formWrapper.querySelector('.gf-sejour-transfer-icon');
        const field = this.formWrapper.querySelector('.gf-sejour-transfer');

        if (icon && field) {
            const wrapper = document.createElement('div');
            wrapper.className = 'transfer-grid-wrapper';

            icon.parentNode.insertBefore(wrapper, icon);
            wrapper.appendChild(icon);
            wrapper.appendChild(field);
        }
    }

    bindEvents() {

        this.formWrapper.addEventListener('change', (e) => {
            this.updateState();
        });

        this.formWrapper.addEventListener('input', (e) => {
            if (e.target.matches('input[type="number"]')) {
                this.updateState();
            }
        });

        // Observer les changements du champ coupon (mis à jour via AJAX)
        this.setupCouponObserver();

        // Écouter les clics sur les boutons Précédent et Suivant
        document.addEventListener('click', (e) => {
            const prevButton = e.target.closest('.gform_previous_button');
            const nextButton = e.target.closest('.gform_next_button');

            if (prevButton || nextButton) {
                this.showSpinner();
            }

            if (prevButton) {
                const pageBeforeClick = this.currentPage;
                this.pendingBackNavigation = true;
                this.pageBeforeNavigation = pageBeforeClick;
                // Attendre que la page change puis reset (délai plus long pour AJAX)
                setTimeout(() => {
                    this.checkAndResetAfterNavigation();
                    this.hideSpinner();
                }, 1000);
            }

            if (nextButton) {
                const pageBeforeClick = this.currentPage;
                this.pageBeforeNavigation = pageBeforeClick;
                // Attendre que la page change puis mettre à jour currentPage
                setTimeout(() => {
                    this.updateCurrentPageAfterNavigation();
                    this.hideSpinner();
                }, 1000);
            }
        }, true); // capture phase

        // Écouter le clic sur le bouton de soumission finale
        document.addEventListener('click', (e) => {
            const submitButton = e.target.closest('.gform_button[type="submit"], .gform_submit_button');
            if (submitButton && this.formWrapper.contains(submitButton)) {
                this.showSpinner();
            }
        });

        // Utiliser jQuery pour écouter les événements Gravity Forms
        if (typeof jQuery !== 'undefined') {
            jQuery(document).on('gform_page_loaded', (event, formId, currentPage) => {
                this.handlePageChange(currentPage);
            });

            // Cacher le spinner lors de la confirmation
            jQuery(document).on('gform_confirmation_loaded', (event, formId) => {
                if (formId == this.formId) {
                    this.hideSpinner();
                }
            });

            // Cacher le spinner en cas d'erreur de validation ou de paiement
            jQuery(document).on('gform_post_render', (event, formId) => {
                if (formId == this.formId) {
                    this.hideSpinner();
                }
            });
        }

        // MutationObserver comme fallback fiable pour détecter les changements de page
        this.setupPageObserver();

        // Observer les erreurs Stripe pour cacher le spinner
        this.setupStripeErrorObserver();
    }

    setupPageObserver() {
        const pages = this.formWrapper.querySelectorAll('.gform_page');

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                    const target = mutation.target;
                    if (target.classList.contains('gform_page')) {
                        // Une page a changé de visibilité
                        const newPage = this.getCurrentPageNumber();
                        if (newPage !== this.currentPage) {
                            this.handlePageChange(newPage);
                        }
                    }
                }
            });
        });

        // Observer tous les éléments .gform_page
        pages.forEach(page => {
            observer.observe(page, { attributes: true, attributeFilter: ['style'] });
        });
    }

    setupStripeErrorObserver() {
        // Observer les erreurs Stripe qui apparaissent dans le DOM
        const stripeField = this.formWrapper.querySelector('.gfield--type-stripe_creditcard, .ginput_container_creditcard');
        if (!stripeField) return;

        const checkForErrors = () => {
            const errorSelectors = [
                '.gfield_validation_message',
                '.validation_message',
                '.gform_validation_errors',
                '.gfield_error',
                '.gform_ajax_spinner',
                '[class*="error"]',
                '.StripeElement--invalid'
            ];
            const errorEl = this.formWrapper.querySelector(errorSelectors.join(', '));
            if (errorEl) {
                this.hideSpinner();
                return true;
            }
            return false;
        };

        const observer = new MutationObserver(() => {
            checkForErrors();
        });

        // Observer le champ Stripe et ses parents pour les erreurs
        observer.observe(stripeField, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

        // Observer aussi le formulaire entier pour les erreurs de validation
        const formBody = this.formWrapper.querySelector('.gform_body');
        if (formBody) {
            observer.observe(formBody, { childList: true, subtree: true });
        }

        // Observer le wrapper entier
        observer.observe(this.formWrapper, { childList: true, subtree: true });

        // Fallback : timeout de sécurité pour cacher le spinner après 30 secondes
        let spinnerTimeout = null;
        const originalShowSpinner = this.showSpinner.bind(this);
        this.showSpinner = () => {
            originalShowSpinner();
            if (spinnerTimeout) clearTimeout(spinnerTimeout);
            spinnerTimeout = setTimeout(() => {
                this.hideSpinner();
            }, 5000);
        };

        const originalHideSpinner = this.hideSpinner.bind(this);
        this.hideSpinner = () => {
            originalHideSpinner();
            if (spinnerTimeout) {
                clearTimeout(spinnerTimeout);
                spinnerTimeout = null;
            }
        };
    }

    setupCouponObserver() {
        // Observer les changements du champ coupon (mis à jour via AJAX par Gravity Forms)
        const couponsContainer = this.formWrapper.querySelector('#gf_coupons_container_1, [id^="gf_coupons_container_"]');
        if (!couponsContainer) return;

        const observer = new MutationObserver(() => {
            this.updateState();
        });

        // Observer les changements dans le conteneur des coupons
        observer.observe(couponsContainer, {
            childList: true,
            subtree: true,
            attributes: true,
            characterData: true
        });
    }

    handlePageChange(newPage) {
        // Trigger event for listening classes
        try {
            this.dispatch(GFormSejourEvents.PAGE_CHANGE, { page: newPage });
        } catch (error) {
            console.error(error);
        }
        // Détecter si on revient en arrière
        if (newPage < this.currentPage || this.pendingBackNavigation) {
            if (newPage < this.currentPage) {
                this.resetStepsAfter(newPage);
            }
        }

        this.pendingBackNavigation = false;
        this.currentPage = newPage;

        this.quantityControls.init();
        this.participantsManager.updateVisibility();
        this.enhanceAccommodationCards();
        this.updateState();
    }

    checkAndResetAfterNavigation() {
        const newPage = this.getCurrentPageNumber();
        const previousPage = this.pageBeforeNavigation || this.currentPage;

        if (newPage < previousPage) {
            this.resetStepsAfter(newPage);
        }

        this.currentPage = newPage;
        this.pageBeforeNavigation = null;
        this.pendingBackNavigation = false;
        this.quantityControls.init();
        this.updateState();
    }

    updateCurrentPageAfterNavigation() {
        const newPage = this.getCurrentPageNumber();
        this.currentPage = newPage;
        this.quantityControls.init();
        this.participantsManager.updateVisibility();
        this.enhanceAccommodationCards();
        this.updateState();
    }

    getCurrentPageNumber() {
        // Essayer plusieurs méthodes pour trouver la page visible
        const pages = this.formWrapper.querySelectorAll('.gform_page');
        for (const page of pages) {
            const style = window.getComputedStyle(page);
            if (style.display !== 'none') {
                const pageId = page.id;
                const match = pageId.match(/gform_page_\d+_(\d+)/);
                if (match) {
                    return parseInt(match[1]);
                }
            }
        }
        return 1;
    }

    resetStepsAfter(pageNumber) {
        // Reset étape 2 (hébergement) si on revient à l'étape 1
        if (pageNumber <= 1) {
            this.resetAccommodation();
        }

        // Reset étape 3 (pensions et transfert) si on revient à l'étape 1 ou 2
        if (pageNumber <= 2) {
            this.resetPensions();
            this.resetTransfer();
        }

        // Mettre à jour le résumé
        this.updateState();
    }

    resetAccommodation() {
        // Décocher tous les hébergements et sélectionner le premier par défaut
        const accommodationField = this.formWrapper.querySelector('.gf-sejour-accommodation');
        if (accommodationField) {
            const inputs = accommodationField.querySelectorAll('input[type="radio"]');
            inputs.forEach((input, index) => {
                input.checked = (index === 0);
            });
        }

        this.state.accommodation = null;
        this.state.accommodationPrice = 0;
    }

    resetPensions() {
        // Remettre les pensions à 0 via classes CSS custom
        const adultField = this.formWrapper.querySelector('.gf-sejour-pension-adult');
        const childField = this.formWrapper.querySelector('.gf-sejour-pension-child');

        if (adultField) {
            const input = adultField.querySelector('.ginput_quantity');
            if (input) {
                input.value = 0;
                this.triggerInputChange(input);
            }
        }

        if (childField) {
            const input = childField.querySelector('.ginput_quantity');
            if (input) {
                input.value = 0;
                this.triggerInputChange(input);
            }
        }

        this.state.adultPensions = 0;
        this.state.childPensions = 0;
    }

    resetTransfer() {
        // Remettre le transfert à 0 via classe CSS custom
        const transferField = this.formWrapper.querySelector('.gf-sejour-transfer');
        if (transferField) {
            const input = transferField.querySelector('.ginput_quantity');
            if (input) {
                input.value = 0;
                this.triggerInputChange(input);
            }
        }

        this.state.transferCount = 0;
    }

    triggerInputChange(input) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    updateState() {
        this.readParticipants();
        this.readAccommodation();
        this.readPensions();
        this.readTransfer();
        this.readPaymentChoice();
        this.readDiscount();
        this.summary.update();
    }

    readParticipants() {
        // Lire le nombre d'adultes
        const adultsField = this.formWrapper.querySelector('.gf-sejour-adults');
        if (adultsField) {
            const qtyInput = adultsField.querySelector('.ginput_quantity, .gf-quantity-input');
            if (qtyInput) {
                const value = parseInt(qtyInput.value) || 1;
                this.state.adults = Math.max(1, value);
            }
        }

        // Lire le nombre d'enfants
        const childrenField = this.formWrapper.querySelector('.gf-sejour-children');
        if (childrenField) {
            const qtyInput = childrenField.querySelector('.ginput_quantity, .gf-quantity-input');
            if (qtyInput) {
                const value = parseInt(qtyInput.value) || 0;
                this.state.children = Math.max(0, value); // Minimum 0 enfant
            }
        }

        // Mettre à jour la quantité d'hébergements quand les participants changent
        this.updateAccommodationQuantity();
    }

    readAccommodation() {
        const accommodationField = this.formWrapper.querySelector('.gf-sejour-accommodation');
        if (accommodationField) {
            const checkedInput = accommodationField.querySelector('input[type="radio"]:checked');
            if (checkedInput) {
                const label = checkedInput.closest('.gchoice')?.querySelector('.acc-title');
                const value = checkedInput.value;
                this.state.accommodation = label ? label.textContent.trim() : value.split('|')[0];

                const pricePart = value.split('|')[1];
                this.state.accommodationPrice = pricePart ? parseFloat(pricePart) || 0 : 0;

                // Le prix hébergement est toujours par personne
                this.state.accommodationPricePerPerson = true;

                // Mettre à jour le champ caché de quantité d'hébergements
                this.updateAccommodationQuantity();
            }
        }
    }

    updateAccommodationQuantity() {
        // Pour gf-sejour-specific : hébergement payant seulement pour les adultes
        // Pour les autres formulaires : tous les participants
        const quantity = this.isSejourSpecific
            ? this.state.adults
            : this.state.adults + this.state.children;

        // Trouver le champ caché gf-nombre-hebergement
        const quantityField = this.formWrapper.querySelector('.gf-nombre-hebergement');
        if (quantityField) {
            const input = quantityField.querySelector('input[type="number"], input.ginput_quantity');
            if (input && parseInt(input.value) !== quantity) {
                input.value = quantity;
                this.triggerInputChange(input);
            }
        }
    }

    readPensions() {
        const adultField = this.formWrapper.querySelector('.gf-sejour-pension-adult');
        const childField = this.formWrapper.querySelector('.gf-sejour-pension-child');

        if (adultField) {
            const input = adultField.querySelector('.ginput_quantity');
            this.state.adultPensions = input ? parseInt(input.value) || 0 : 0;
        }
        if (childField) {
            const input = childField.querySelector('.ginput_quantity');
            this.state.childPensions = input ? parseInt(input.value) || 0 : 0;
        }
    }

    readTransfer() {
        const transferField = this.formWrapper.querySelector('.gf-sejour-transfer');
        if (transferField) {
            const input = transferField.querySelector('.ginput_quantity');
            this.state.transferCount = input ? parseInt(input.value) || 0 : 0;
        }
    }

    readPaymentChoice() {
        const paymentField = this.formWrapper.querySelector('.gf-choix-paiement');
        if (paymentField) {
            const allInputs = paymentField.querySelectorAll('input[type="radio"]');
            const checkedInput = paymentField.querySelector('input[type="radio"]:checked');

            if (checkedInput && allInputs.length > 0) {
                // Premier choix = acompte (30%), autre = paiement intégral (100%)
                this.state.isFullPayment = (checkedInput !== allInputs[0]);
            } else {
                // Par défaut, premier choix = acompte
                this.state.isFullPayment = false;
            }
        }
    }

    readDiscount() {
        // Lire les coupons depuis le champ caché gf_coupons_X
        const couponsInput = this.formWrapper.querySelector('input[name^="gf_coupons_"]');
        this.state.discountAmount = 0;
        this.state.discountName = '';
        this.state.couponCode = '';
        this.state.couponPercent = '';

        if (couponsInput && couponsInput.value) {
            try {
                const couponsData = JSON.parse(couponsInput.value);
                const subtotal = this.calculateSubtotal();

                // Parcourir tous les coupons appliqués
                for (const code in couponsData) {
                    const coupon = couponsData[code];
                    this.state.couponCode = code;
                    if (coupon.type === 'percentage') {
                        this.state.discountAmount += subtotal * (coupon.amount / 100);
                        this.state.discountName = coupon.name || `-${coupon.amount}%`;
                        this.state.couponPercent = `${coupon.amount}%`;
                    } else if (coupon.type === 'flat') {
                        this.state.discountAmount += coupon.amount;
                        this.state.discountName = coupon.name || `-${coupon.amount}€`;
                        this.state.couponPercent = `-${coupon.amount}€`;
                    }
                }

            } catch (e) {
                // JSON invalide, ignorer
            }
        }

        // Mettre à jour l'affichage du coupon GF en temps réel
        this.updateGFCouponDisplay();
    }

    updateGFCouponDisplay() {
        // Mettre à jour l'affichage du montant de la remise dans le widget GF Coupons
        const couponDiscountEl = this.formWrapper.querySelector('.gf_coupon_discount');
        if (couponDiscountEl && this.state.discountAmount > 0) {
            const expectedValue = `-${this.formatPrice(this.state.discountAmount)}`;
            if (couponDiscountEl.textContent !== expectedValue) {
                couponDiscountEl.textContent = expectedValue;
            }
        }

        // Mettre à jour le nom du coupon : "CODE (XX%)"
        const couponNameEl = this.formWrapper.querySelector('.gf_coupon_name');
        if (couponNameEl && this.state.couponCode) {
            const expectedName = `${this.state.couponCode} (${this.state.couponPercent})`;
            if (couponNameEl.textContent !== expectedName) {
                couponNameEl.textContent = expectedName;
            }
        }

        // Mettre à jour le total affiché dans le widget GF Coupons
        const couponTotalEl = this.formWrapper.querySelector('.gf_coupon_total');
        if (couponTotalEl) {
            const total = this.calculateTotal();
            const deposit = this.calculateDeposit();
            // Afficher le montant à payer (acompte ou total)
            const amountToPay = this.state.isFullPayment ? total : deposit;
            const expectedTotal = this.formatPrice(amountToPay);
            if (couponTotalEl.textContent !== expectedTotal) {
                couponTotalEl.textContent = expectedTotal;
            }
        }
    }

    calculateSubtotal() {
        // Calcul du sous-total avant remise
        const adultsTotal = this.state.adults * this.prices.adult;
        const childrenTotal = this.state.children * this.prices.child;

        let accommodationTotal = 0;
        if (this.state.accommodationPrice > 0) {
            if (this.isSejourSpecific) {
                // Pour le formulaire spécifique : hébergement payant seulement pour les adultes
                accommodationTotal = this.state.accommodationPrice * this.state.adults;
            } else {
                // Pour les autres formulaires : hébergement payant pour tous les participants
                const totalParticipants = this.state.adults + this.state.children;
                accommodationTotal = this.state.accommodationPrice * totalParticipants;
            }
        }

        const adultPensionTotal = this.state.adultPensions * this.prices.adultPension;
        const childPensionTotal = this.state.childPensions * this.prices.childPension;
        const transferTotal = this.state.transferCount * this.prices.transfer;

        return adultsTotal + childrenTotal + accommodationTotal + adultPensionTotal + childPensionTotal + transferTotal;
    }

    calculateTotal() {
        const subtotal = this.calculateSubtotal();
        return Math.max(0, subtotal - this.state.discountAmount);
    }

    calculateDeposit() {
        const total = this.calculateTotal();
        // Premier choix = acompte (30%), autre choix = 100%
        if (this.state.isFullPayment) {
            return total;
        }
        return total * this.config.depositPercentage;
    }

    formatPrice(amount) {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    }


}


// Reset scroll au chargement de l'iframe - UNIQUEMENT pour les formulaires séjour spécifiques
if (document.body && document.body.classList.contains('page-gravity') && document.querySelector('.gf-sejour-specific_wrapper')) {
    // Reset immédiatement
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    console.log('✅ Scroll reseté immédiatement (sejour-specific)');
}

// Reset aussi au load complet
window.addEventListener('load', function () {
    if (document.body.classList.contains('page-gravity') && document.querySelector('.gf-sejour-specific_wrapper')) {
        window.scrollTo(0, 0);
        console.log('✅ Scroll reseté au load (sejour-specific)');
    }
});

// Reset au DOMContentLoaded
document.addEventListener('DOMContentLoaded', function () {
    if (document.body.classList.contains('page-gravity') && document.querySelector('.gf-sejour-specific_wrapper')) {
        window.scrollTo(0, 0);
        console.log('✅ Scroll reseté au DOMContentLoaded (sejour-specific)');
    }
});
